import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import tesoreriaService from '../../services/tesoreriaService';
import ChequeTerceroDetalleDialog from '../../components/Tesoreria/ChequeTerceroDetalleDialog';
import ChequeTerceroRechazoDialog from '../../components/Tesoreria/ChequeTerceroRechazoDialog';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import ProveedorSearch from '../../components/Tesoreria/ProveedorSearch';
import {
  etiquetaEstadoChequeTercero,
  etiquetaTipoSoporteCheque,
  resolverEstadoChequeTercero,
} from '../../utils/chequeTerceroEstado';
import {
  formatClienteCheque,
  formatCuentaDepositoCheque,
  formatDestinoSalidaCheque,
  formatProveedorCheque,
} from '../../utils/chequeTerceroDisplay';
import { formatCuit } from '../../utils/formatters';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import ChequesTerceroFiltrosPanel from './chequesTercero/ChequesTerceroFiltrosPanel';
import ChequesTerceroFiltrosActivos, {
  type FiltroActivoChip,
  buildEstadoChipLabel,
} from './chequesTercero/ChequesTerceroFiltrosActivos';
import ChequesTerceroLista from './chequesTercero/ChequesTerceroLista';
import ChequesTerceroResumenBar from './chequesTercero/ChequesTerceroResumenBar';
import {
  buildChequesTerceroParams,
  mapResumenFromApi,
} from './chequesTercero/buildChequesTerceroParams';
import type {
  BancoOption,
  ChequeTercero,
  ChequesTerceroFiltrosStored,
  ChequesTerceroResumen,
  McpOption,
  SucursalBanco,
  VencimientoRapido,
} from './chequesTercero/types';
import { VISTA_COMPACTA_STORAGE_KEY } from './chequesTercero/types';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  try {
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

const getEstadoTexto = (cheque: ChequeTercero) =>
  etiquetaEstadoChequeTercero(resolverEstadoChequeTercero(cheque));

const ChequesTercero: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const loadStored = () =>
    loadTesoreriaFiltros<ChequesTerceroFiltrosStored>(TESORERIA_FILTROS_KEYS.chequesTercero);

  const [cheques, setCheques] = useState<ChequeTercero[]>([]);
  const [resumen, setResumen] = useState<ChequesTerceroResumen | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 20));
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [exportingExcel, setExportingExcel] = useState(false);

  const [bancos, setBancos] = useState<BancoOption[]>([]);
  const [sucursales, setSucursales] = useState<SucursalBanco[]>([]);
  const [listaMcps, setListaMcps] = useState<McpOption[]>([]);

  const [nroBanco, setNroBanco] = useState(() => storedString(loadStored(), 'nroBanco', ''));
  const [bancoNombre, setBancoNombre] = useState(() => storedString(loadStored(), 'bancoNombre', ''));
  const [nroSucursal, setNroSucursal] = useState(() => storedString(loadStored(), 'nroSucursal', ''));
  const [fechaIngresoDesde, setFechaIngresoDesde] = useState(() =>
    storedString(loadStored(), 'fechaIngresoDesde', '')
  );
  const [fechaIngresoHasta, setFechaIngresoHasta] = useState(() =>
    storedString(loadStored(), 'fechaIngresoHasta', '')
  );
  const [fechaSalidaDesde, setFechaSalidaDesde] = useState(() =>
    storedString(loadStored(), 'fechaSalidaDesde', '')
  );
  const [fechaSalidaHasta, setFechaSalidaHasta] = useState(() =>
    storedString(loadStored(), 'fechaSalidaHasta', '')
  );
  const [fechaVencimientoDesde, setFechaVencimientoDesde] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoDesde', '')
  );
  const [fechaVencimientoHasta, setFechaVencimientoHasta] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoHasta', '')
  );
  const [importeDesde, setImporteDesde] = useState(() => storedString(loadStored(), 'importeDesde', ''));
  const [importeHasta, setImporteHasta] = useState(() => storedString(loadStored(), 'importeHasta', ''));
  const [mcp, setMcp] = useState(() => storedString(loadStored(), 'mcp', ''));
  const [estado, setEstado] = useState(() => storedString(loadStored(), 'estado', ''));
  const [filtroTipoSoporte, setFiltroTipoSoporte] = useState(() =>
    storedString(loadStored(), 'filtroTipoSoporte', '')
  );
  const [vencimientoRapido, setVencimientoRapido] = useState<VencimientoRapido>(() =>
    (storedString(loadStored(), 'vencimientoRapido', '') as VencimientoRapido) || ''
  );
  const [nroCliente, setNroCliente] = useState<number | null>(() => {
    const v = loadStored().nroCliente;
    return typeof v === 'number' && v > 0 ? v : null;
  });
  const [nombreCliente, setNombreCliente] = useState(() => storedString(loadStored(), 'nombreCliente', ''));
  const [nroProveedor, setNroProveedor] = useState<number | null>(() => {
    const v = loadStored().nroProveedor;
    return typeof v === 'number' && v > 0 ? v : null;
  });
  const [nombreProveedor, setNombreProveedor] = useState(() =>
    storedString(loadStored(), 'nombreProveedor', '')
  );
  const [nroBancoDeposito, setNroBancoDeposito] = useState(() =>
    storedString(loadStored(), 'nroBancoDeposito', '')
  );
  const [bancoDepositoNombre, setBancoDepositoNombre] = useState(() =>
    storedString(loadStored(), 'bancoDepositoNombre', '')
  );
  const [nroCuentaDeposito, setNroCuentaDeposito] = useState(() =>
    storedString(loadStored(), 'nroCuentaDeposito', '')
  );
  const [vistaCompacta, setVistaCompacta] = useState(() => {
    const stored = loadStored().vistaCompacta;
    if (typeof stored === 'boolean') return stored;
    const ls = localStorage.getItem(VISTA_COMPACTA_STORAGE_KEY);
    return ls !== 'false';
  });

  const [openBancoDialog, setOpenBancoDialog] = useState(false);
  const [bancoDialogTarget, setBancoDialogTarget] = useState<'cheque' | 'deposito'>('cheque');
  const [bancoSearch, setBancoSearch] = useState('');
  const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
  const [bancoPage, setBancoPage] = useState(1);
  const [bancoLoading, setBancoLoading] = useState(false);
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [chequeSeleccionado, setChequeSeleccionado] = useState<ChequeTercero | null>(null);
  const [rechazoCheque, setRechazoCheque] = useState<ChequeTercero | null>(null);
  const [detalleReloadToken, setDetalleReloadToken] = useState(0);

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.chequesTercero,
    {
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaSalidaDesde,
      fechaSalidaHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
      filtroTipoSoporte,
      vencimientoRapido,
      nroCliente: nroCliente ?? '',
      nombreCliente,
      nroProveedor: nroProveedor ?? '',
      nombreProveedor,
      nroBancoDeposito,
      bancoDepositoNombre,
      nroCuentaDeposito,
      vistaCompacta,
    },
    [
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaSalidaDesde,
      fechaSalidaHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
      filtroTipoSoporte,
      vencimientoRapido,
      nroCliente,
      nombreCliente,
      nroProveedor,
      nombreProveedor,
      nroBancoDeposito,
      bancoDepositoNombre,
      nroCuentaDeposito,
      vistaCompacta,
    ]
  );

  useEffect(() => {
    localStorage.setItem(VISTA_COMPACTA_STORAGE_KEY, String(vistaCompacta));
  }, [vistaCompacta]);

  const resetPage = useCallback(() => setPage(0), []);

  const filtrosParams = useMemo(
    () =>
      buildChequesTerceroParams({
        pageNumber: page + 1,
        pageSize: Math.min(rowsPerPage, 20),
        searchTerm,
        nroBanco,
        nroSucursal,
        fechaIngresoDesde,
        fechaIngresoHasta,
        fechaSalidaDesde,
        fechaSalidaHasta,
        fechaVencimientoDesde,
        fechaVencimientoHasta,
        importeDesde,
        importeHasta,
        mcp,
        estado,
        nroCliente,
        nroProveedor,
        nroBancoDeposito,
        nroCuentaDeposito,
        filtroTipoSoporte,
      }),
    [
      page,
      rowsPerPage,
      searchTerm,
      nroBanco,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaSalidaDesde,
      fechaSalidaHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
      nroCliente,
      nroProveedor,
      nroBancoDeposito,
      nroCuentaDeposito,
      filtroTipoSoporte,
    ]
  );

  useEffect(() => {
    const loadSucursales = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get('/api/Tesoreria/sucursales-banco', {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        setSucursales(res.data || []);
      } catch {
        /* ignore */
      }
    };
    void loadSucursales();
  }, []);

  useEffect(() => {
    const loadMcps = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get('/api/Tesoreria/cheques-terceros/mcps', {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        const data = res.data || [];
        setListaMcps(
          Array.isArray(data)
            ? data.map((it: { mcp?: string; Mcp?: string; descripcion?: string; Descripcion?: string }) => ({
                mcp: it.mcp ?? it.Mcp ?? '',
                descripcion: it.descripcion ?? it.Descripcion,
              }))
            : []
        );
      } catch {
        setListaMcps([]);
      }
    };
    void loadMcps();
  }, []);

  useEffect(() => {
    const loadBancos = async (reset = false) => {
      try {
        setBancoLoading(true);
        const items = await tesoreriaService.getBancosPaged({
          searchTerm: bancoSearch || undefined,
          pageNumber: reset ? 1 : bancoPage,
          pageSize: 20,
        });
        setBancos((prev) => (reset ? items : [...prev, ...items]));
        if (reset) setBancoPage(1);
      } catch {
        /* ignore */
      } finally {
        setBancoLoading(false);
      }
    };
    if (openBancoDialog) {
      const timer = setTimeout(() => void loadBancos(true), bancoSearch ? 500 : 0);
      return () => clearTimeout(timer);
    }
  }, [openBancoDialog, bancoSearch, bancoPage]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await api.get('/api/Tesoreria/cheques-terceros', {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
          params: filtrosParams,
        });
        const data = response.data;
        let chequesArray: ChequeTercero[] = [];
        let count = 0;
        if (data?.chequesTerceros && Array.isArray(data.chequesTerceros)) {
          chequesArray = data.chequesTerceros;
          count = data.totalCount ?? chequesArray.length;
        } else if (data?.items && Array.isArray(data.items)) {
          chequesArray = data.items;
          count = data.totalCount ?? chequesArray.length;
        } else if (Array.isArray(data)) {
          chequesArray = data;
          count = data.length;
        }
        setCheques(chequesArray);
        setTotalCount(count);
        setResumen(mapResumenFromApi(data));
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        enqueueSnackbar(err.response?.data?.message || 'No se pudieron cargar los cheques.', {
          variant: 'error',
        });
        setCheques([]);
        setTotalCount(0);
        setResumen(null);
      } finally {
        setLoading(false);
      }
    };
    const timer = setTimeout(() => void loadData(), searchTerm.trim() ? 500 : 0);
    return () => clearTimeout(timer);
  }, [filtrosParams, searchTerm, enqueueSnackbar, detalleReloadToken]);

  const sucursalesFiltradas = nroBanco
    ? sucursales.filter((s) => s.nroBanco === parseInt(nroBanco, 10))
    : [];

  const handleClearFilters = () => {
    setNroBanco('');
    setBancoNombre('');
    setNroSucursal('');
    setFechaIngresoDesde('');
    setFechaIngresoHasta('');
    setFechaSalidaDesde('');
    setFechaSalidaHasta('');
    setFechaVencimientoDesde('');
    setFechaVencimientoHasta('');
    setImporteDesde('');
    setImporteHasta('');
    setMcp('');
    setEstado('');
    setFiltroTipoSoporte('');
    setVencimientoRapido('');
    setNroCliente(null);
    setNombreCliente('');
    setNroProveedor(null);
    setNombreProveedor('');
    setNroBancoDeposito('');
    setBancoDepositoNombre('');
    setNroCuentaDeposito('');
    setSearchTerm('');
    resetPage();
  };

  const openBancoDialogFor = (target: 'cheque' | 'deposito') => {
    setBancoDialogTarget(target);
    setBancoSeleccionadoId(null);
    setOpenBancoDialog(true);
  };

  const handleBancoSelect = () => {
    const banco = bancos.find((b) => b.id === bancoSeleccionadoId);
    if (banco?.nroBanco) {
      if (bancoDialogTarget === 'deposito') {
        setNroBancoDeposito(banco.nroBanco.toString());
        setBancoDepositoNombre(`${banco.nombre} (${banco.nroBanco})`);
      } else {
        setNroBanco(banco.nroBanco.toString());
        setBancoNombre(`${banco.nombre} (${banco.nroBanco})`);
        setNroSucursal('');
      }
      resetPage();
    }
    setOpenBancoDialog(false);
    setBancoSearch('');
  };

  const filterChips = useMemo((): FiltroActivoChip[] => {
    const chips: FiltroActivoChip[] = [];
    const add = (id: string, label: string, clear: () => void) =>
      chips.push({ id, label, onDelete: () => { clear(); resetPage(); } });

    if (searchTerm.trim()) add('search', `Búsqueda: ${searchTerm.trim()}`, () => setSearchTerm(''));
    if (estado) add('estado', buildEstadoChipLabel(estado), () => setEstado(''));
    if (filtroTipoSoporte === 'fisicos') add('soporte', 'Soporte: Físicos', () => setFiltroTipoSoporte(''));
    if (filtroTipoSoporte === 'electronico') add('soporte', 'Soporte: Electrónicos', () => setFiltroTipoSoporte(''));
    if (nroCliente)
      add('cliente', `Cliente: ${nombreCliente} — ${nroCliente}`, () => {
        setNroCliente(null);
        setNombreCliente('');
      });
    if (nroProveedor)
      add('proveedor', `Proveedor: ${nombreProveedor} — ${nroProveedor}`, () => {
        setNroProveedor(null);
        setNombreProveedor('');
      });
    if (nroBanco) add('banco', `Banco: ${bancoNombre}`, () => { setNroBanco(''); setBancoNombre(''); setNroSucursal(''); });
    if (nroBancoDeposito) add('bancoDep', `Depósito: ${bancoDepositoNombre}`, () => { setNroBancoDeposito(''); setBancoDepositoNombre(''); });
    if (nroCuentaDeposito) add('cuentaDep', `Cta. depósito: ${nroCuentaDeposito}`, () => setNroCuentaDeposito(''));
    if (vencimientoRapido) {
      const labels: Record<string, string> = {
        este_mes: 'Vencen este mes',
        proximos_7: 'Próximos 7 días',
        vencidos: 'Vencidos',
      };
      add('vencRapido', labels[vencimientoRapido] ?? vencimientoRapido, () => {
        setVencimientoRapido('');
        setFechaVencimientoDesde('');
        setFechaVencimientoHasta('');
      });
    }
    if (mcp.trim()) add('mcp', `MCP: ${mcp}`, () => setMcp(''));
    return chips;
  }, [
    searchTerm, estado, filtroTipoSoporte, nroCliente, nombreCliente, nroProveedor, nombreProveedor,
    nroBanco, bancoNombre, nroBancoDeposito, bancoDepositoNombre, nroCuentaDeposito,
    vencimientoRapido, mcp, resetPage,
  ]);

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      enqueueSnackbar('Obteniendo datos para exportar...', { variant: 'info' });
      const token = localStorage.getItem('token');
      const response = await api.get('/api/Tesoreria/cheques-terceros', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: buildChequesTerceroParams({
          pageNumber: 1,
          pageSize: 1000,
          searchTerm,
          nroBanco,
          nroSucursal,
          fechaIngresoDesde,
          fechaIngresoHasta,
          fechaSalidaDesde,
          fechaSalidaHasta,
          fechaVencimientoDesde,
          fechaVencimientoHasta,
          importeDesde,
          importeHasta,
          mcp,
          estado,
          nroCliente,
          nroProveedor,
          nroBancoDeposito,
          nroCuentaDeposito,
          filtroTipoSoporte,
        }),
      });
      const data = response.data;
      let items: ChequeTercero[] = [];
      if (data?.chequesTerceros) items = data.chequesTerceros;
      else if (data?.items) items = data.items;
      else if (Array.isArray(data)) items = data;
      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      const datos = items.map((c) => ({
        'Nro. Carga': c.nroCarga,
        'Nro. Cheque': c.nroCheque,
        'Estado': getEstadoTexto(c),
        'Soporte': etiquetaTipoSoporteCheque(c),
        'Banco': c.nombreBanco?.trim() || '',
        'Cliente': formatClienteCheque(c),
        'Destino / Salida': formatDestinoSalidaCheque(c),
        'Importe': c.importe || 0,
        'Fecha Vencimiento': formatDate(c.fechaVencimiento),
        'Comprobante Ingreso': c.comprobanteIngreso?.trim() || '',
        'Comprobante Salida': c.comprobanteSalida?.trim() || '',
        'Proveedor': formatProveedorCheque(c),
        'Cuenta depósito': formatCuentaDepositoCheque(c),
        'Librador': c.librador?.trim() || '',
        'CUIT Librador': formatCuit(c.nroLibrador?.trim() || '') || '',
        'MCP': c.mcp?.trim() || '',
      }));
      const ws = XLSX.utils.json_to_sheet(datos);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cheques de Tercero');
      XLSX.writeFile(wb, `Cheques_Tercero_${new Date().toISOString().split('T')[0]}.xlsx`);
      enqueueSnackbar(`Exportado (${items.length} registros)`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Error al exportar', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tesoreria/informes')} sx={{ textTransform: 'none' }}>
                Informes
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Cheques de Tercero
              </Typography>
            </Stack>
            <Tooltip title="Exporta hasta 1.000 registros con los filtros actuales">
              <span>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={exportingExcel ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                  onClick={() => void handleExportExcel()}
                  disabled={loading || exportingExcel || totalCount === 0}
                  sx={{ textTransform: 'none' }}
                >
                  {exportingExcel ? 'Exportando…' : 'Exportar Excel'}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <TextField
            fullWidth
            placeholder="Cheque, librador, cliente, proveedor, comprobante…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); resetPage(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 560, mb: 2 }}
            size="small"
          />

          <ChequesTerceroFiltrosPanel
            isMobile={isMobile}
            vistaCompacta={vistaCompacta}
            onVistaCompactaChange={setVistaCompacta}
            estado={estado}
            onEstadoChange={(v) => { setEstado(v); resetPage(); }}
            filtroTipoSoporte={filtroTipoSoporte}
            onFiltroTipoSoporteChange={(v) => { setFiltroTipoSoporte(v); resetPage(); }}
            vencimientoRapido={vencimientoRapido}
            onVencimientoRapidoChange={(v) => { setVencimientoRapido(v); resetPage(); }}
            nroCliente={nroCliente}
            nombreCliente={nombreCliente}
            onClienteSelect={(n, nom) => { setNroCliente(n); setNombreCliente(nom); resetPage(); }}
            onOpenClienteSearch={() => setOpenClienteSearch(true)}
            bancoNombre={bancoNombre}
            onOpenBancoCheque={() => openBancoDialogFor('cheque')}
            nroSucursal={nroSucursal}
            onNroSucursalChange={(v) => { setNroSucursal(v); resetPage(); }}
            sucursalesFiltradas={sucursalesFiltradas}
            nroBanco={nroBanco}
            nroProveedor={nroProveedor}
            nombreProveedor={nombreProveedor}
            onProveedorSelect={(n, nom) => { setNroProveedor(n); setNombreProveedor(nom); resetPage(); }}
            onOpenProveedorSearch={() => setOpenProveedorSearch(true)}
            bancoDepositoNombre={bancoDepositoNombre}
            onOpenBancoDeposito={() => openBancoDialogFor('deposito')}
            nroCuentaDeposito={nroCuentaDeposito}
            onNroCuentaDepositoChange={(v) => { setNroCuentaDeposito(v); resetPage(); }}
            fechaIngresoDesde={fechaIngresoDesde}
            fechaIngresoHasta={fechaIngresoHasta}
            onFechaIngresoDesde={(v) => { setFechaIngresoDesde(v); setVencimientoRapido(''); resetPage(); }}
            onFechaIngresoHasta={(v) => { setFechaIngresoHasta(v); resetPage(); }}
            fechaSalidaDesde={fechaSalidaDesde}
            fechaSalidaHasta={fechaSalidaHasta}
            onFechaSalidaDesde={(v) => { setFechaSalidaDesde(v); resetPage(); }}
            onFechaSalidaHasta={(v) => { setFechaSalidaHasta(v); resetPage(); }}
            fechaVencimientoDesde={fechaVencimientoDesde}
            fechaVencimientoHasta={fechaVencimientoHasta}
            onFechaVencimientoDesde={(v) => { setFechaVencimientoDesde(v); setVencimientoRapido(''); resetPage(); }}
            onFechaVencimientoHasta={(v) => { setFechaVencimientoHasta(v); setVencimientoRapido(''); resetPage(); }}
            importeDesde={importeDesde}
            importeHasta={importeHasta}
            onImporteDesde={(v) => { setImporteDesde(v); resetPage(); }}
            onImporteHasta={(v) => { setImporteHasta(v); resetPage(); }}
            mcp={mcp}
            listaMcps={listaMcps}
            onMcpChange={(v) => { setMcp(v); resetPage(); }}
            onClearFilters={handleClearFilters}
          />

          <ChequesTerceroFiltrosActivos chips={filterChips} totalCount={totalCount} loading={loading} />
          <ChequesTerceroResumenBar resumen={resumen} loading={loading} formatCurrency={formatCurrency} />

          <ChequesTerceroLista
            cheques={cheques}
            loading={loading}
            isMobile={isMobile}
            vistaCompacta={vistaCompacta}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            searchTerm={searchTerm}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Math.min(parseInt(e.target.value, 10), 20));
              resetPage();
            }}
            onVerDetalle={setChequeSeleccionado}
            onRechazar={setRechazoCheque}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        </CardContent>
      </Card>

      <ChequeTerceroDetalleDialog
        open={!!chequeSeleccionado}
        onClose={() => setChequeSeleccionado(null)}
        cheque={chequeSeleccionado}
        onSaved={() => setDetalleReloadToken((t) => t + 1)}
      />

      <ChequeTerceroRechazoDialog
        open={!!rechazoCheque}
        nroCarga={rechazoCheque?.nroCarga ?? null}
        onClose={() => setRechazoCheque(null)}
        onSuccess={() => {
          setRechazoCheque(null);
          setDetalleReloadToken((t) => t + 1);
        }}
      />

      <Dialog open={openBancoDialog} onClose={() => setOpenBancoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bancoDialogTarget === 'deposito' ? 'Banco de depósito' : 'Banco del cheque'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar banco..."
            value={bancoSearch}
            onChange={(e) => setBancoSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{ maxHeight: 360, overflowY: 'auto' }}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollHeight - el.scrollTop === el.clientHeight && !bancoLoading) {
                setBancoPage((p) => p + 1);
              }
            }}
          >
            <Grid container spacing={1}>
              {bancos.map((banco) => (
                <Grid item xs={12} key={banco.id}>
                  <Card
                    variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setBancoSeleccionadoId(banco.id)}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {bancoLoading && (
                <Grid item xs={12}>
                  <CircularProgress size={24} />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBancoDialog(false)}>Cancelar</Button>
          <Button variant="contained" disabled={bancoSeleccionadoId == null} onClick={handleBancoSelect}>
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteSearch
        open={openClienteSearch}
        onClose={() => setOpenClienteSearch(false)}
        excluirInactivos={false}
        onSelect={(c) => {
          setNroCliente(c.nroCliente);
          setNombreCliente(c.nombre ?? '');
          setOpenClienteSearch(false);
          resetPage();
        }}
      />

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={(p) => {
          setNroProveedor(p.nroProveedor);
          setNombreProveedor(p.nombre ?? '');
          setOpenProveedorSearch(false);
          resetPage();
        }}
      />
    </Box>
  );
};

export default ChequesTercero;
